import dayjs from "dayjs";

export const fmtDate = (d) => (d ? dayjs(d).format("DD/MM/YYYY") : "—");
export const fmtDateTime = (d) => (d ? dayjs(d).format("DD/MM/YYYY HH:mm") : "—");
export const fmtDay = (d) => (d ? dayjs(d).format("ddd DD MMM") : "—");
export const relDays = (d) => {
  if (!d) return null;
  return dayjs(d).startOf("day").diff(dayjs().startOf("day"), "day");
};

export const fmtRelative = (d) => {
  if (!d) return "Never";
  const now = dayjs();
  const then = dayjs(d);
  const mins = now.diff(then, "minute");
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = now.diff(then, "hour");
  if (hrs < 24) return `${hrs}h ago`;
  const days = now.diff(then, "day");
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return then.format("DD/MM/YYYY");
};

export const fmtCurrency = (n) =>
  n == null ? "—" : "€" + Number(n).toLocaleString("en-IE", { maximumFractionDigits: 0 });

export const fmtValue = (v, unit) => {
  if (v == null) return "—";
  if (unit === "€") return fmtCurrency(v);
  if (unit === "%") return `${v}%`;
  if (unit === "Days") return `${v} days`;
  if (unit === "Hours") return `${v} hrs`;
  if (unit === "Score") return `${v}`;
  return `${Number(v).toLocaleString("en-IE")}`;
};

// RAG helpers
export const RAG = {
  green: { bg: "bg-[#3D8B65]/10", text: "text-[#3D8B65]", dot: "bg-[#3D8B65]", solid: "#3D8B65" },
  amber: { bg: "bg-[#D99A32]/12", text: "text-[#B87F1E]", dot: "bg-[#D99A32]", solid: "#D99A32" },
  red: { bg: "bg-[#C95555]/10", text: "text-[#C95555]", dot: "bg-[#C95555]", solid: "#C95555" },
  blue: { bg: "bg-[#4A78A8]/10", text: "text-[#4A78A8]", dot: "bg-[#4A78A8]", solid: "#4A78A8" },
  grey: { bg: "bg-[#E4E8EF]", text: "text-[#1E2D44]/60", dot: "bg-[#8A97AB]", solid: "#8A97AB" },
};

export const scoreRag = (s) => {
  if (s == null) return "grey";
  if (s < 40) return "red";
  if (s < 60) return "amber";
  if (s < 75) return "amber";
  return "green";
};
export const scoreBand = (s) => {
  if (s == null) return "Not assessed";
  if (s < 40) return "High risk";
  if (s < 60) return "Improvement required";
  if (s < 75) return "Developing";
  if (s < 90) return "Performing well";
  return "High-performing";
};

export const CLASSIFICATION = {
  healthy: { label: "Healthy", rag: "green" },
  monitor: { label: "Monitor", rag: "blue" },
  attention: { label: "Attention required", rag: "amber" },
  high_risk: { label: "High risk", rag: "red" },
};

export const ACTION_STATUS = {
  not_started: { label: "Not started", rag: "grey" },
  in_progress: { label: "In progress", rag: "blue" },
  waiting: { label: "Waiting", rag: "amber" },
  blocked: { label: "Blocked", rag: "red" },
  awaiting_review: { label: "Awaiting review", rag: "amber" },
  completed: { label: "Completed", rag: "green" },
  cancelled: { label: "Cancelled", rag: "grey" },
};
export const PRIORITY_STATUS = {
  not_started: { label: "Not started", rag: "grey" },
  in_progress: { label: "In progress", rag: "blue" },
  on_track: { label: "On track", rag: "green" },
  at_risk: { label: "At risk", rag: "amber" },
  blocked: { label: "Blocked", rag: "red" },
  completed: { label: "Completed", rag: "green" },
};
export const PLAN_STATUS = {
  draft: { label: "Draft", rag: "grey" },
  active: { label: "Active", rag: "green" },
  at_risk: { label: "At risk", rag: "amber" },
  completed: { label: "Completed", rag: "blue" },
  archived: { label: "Archived", rag: "grey" },
};
export const LEVEL = {
  low: { label: "Low", rag: "grey" },
  normal: { label: "Normal", rag: "blue" },
  high: { label: "High", rag: "amber" },
  critical: { label: "Critical", rag: "red" },
};
export const URGENCY = {
  low: { label: "Low", rag: "grey" },
  medium: { label: "Medium", rag: "blue" },
  high: { label: "High", rag: "amber" },
  critical: { label: "Critical", rag: "red" },
};
export const ISSUE_STATUS = {
  open: { label: "Open", rag: "blue" },
  under_review: { label: "Under review", rag: "amber" },
  decision_required: { label: "Decision required", rag: "amber" },
  in_progress: { label: "In progress", rag: "blue" },
  resolved: { label: "Resolved", rag: "green" },
  closed: { label: "Closed", rag: "grey" },
};
export const ORG_STATUS = {
  onboarding: { label: "Onboarding", rag: "blue" },
  active: { label: "Active", rag: "green" },
  attention_required: { label: "Attention required", rag: "amber" },
  paused: { label: "Paused", rag: "grey" },
  completed: { label: "Completed", rag: "blue" },
  archived: { label: "Archived", rag: "grey" },
};

export const CHART_COLORS = ["#FFB000", "#3D8B65", "#4A78A8", "#D99A32", "#C95555", "#8a6a3f"];

export const BUSINESS_AREAS = [
  "Strategy and Direction",
  "Leadership and Management",
  "Finance and Profitability",
  "Sales and Pipeline",
  "Marketing",
  "People and HR",
  "Operations and Systems",
  "Customer Experience",
  "Compliance and Risk",
  "Technology and Automation",
];

export const OVERDUE_REASONS = [
  "Lack of time",
  "Waiting on another person",
  "Lack of information",
  "Lack of budget",
  "Priority changed",
  "Technical issue",
  "Decision required",
  "Other",
];
